import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { PromoBannerSlot, SIDEBAR_BANNER_DESKTOP_MIN_WIDTH, richHtmlSx, useGetByDevice } from '@onesource/components';
import type { TContentBlockBase } from '@onesource/schemas';
import {
  ContentBlockType,
  EContentStatus,
  LessonStatus,
  LessonTemplateType,
  PromoBannerPosition,
} from '@onesource/schemas';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { MdFolder, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { DraftBadge } from 'src/components/draft-badge';

import { ProgressCard } from '#/features/community-hub/components/progress-card';
import { QuizCard } from '#/features/community-hub/components/quiz-card';
import { usePreviewMode } from '#/features/community-hub/hooks/use-preview-mode';

import { DocumentCard } from './document-card';
import { EmbedBlockContent } from './embed-block-content';
import { EmbedCard } from './embed-card';
import { FormCard } from './form-card';
import { GcsUploadCard } from './gcs-upload-card';
import { LessonContentSummary } from './lesson-content-summary';
import { LiveSessionCard } from './live-session-card';
import { PdfBlockViewer } from './pdf-block-viewer';
import { RedirectCard } from './redirect-card';
import { SubLessonsSection } from './sub-lessons-section';
import { TextBlockContent } from './text-block-content';
import { TextCard } from './text-card';
import type { LessonDetailViewProps } from './types';
import { VideoBlockPlayer } from './video-block-player';
import { VideoCard } from './video-card';
import { VideoPlaylist } from './video-playlist';

export function LessonDetailView({
  lesson,
  progressPercent,
  breadcrumbs,
  onNavigateToBlock,
  onNavigateToSubLesson,
}: LessonDetailViewProps) {
  const theme = useTheme();
  const { isPreview } = usePreviewMode();
  const { isMobile } = useGetByDevice();

  // The description is long on most lessons — on mobile it collapses behind a
  // Read More toggle so the video and the lesson sections stay reachable.
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const isDescriptionCollapsed = isMobile && !isDescriptionExpanded;

  const showProgressBar = lesson.showProgressBar !== false;

  // Inline template renders video & text directly on the lesson page (no
  // per-block navigation); other block types keep their navigating cards.
  const isInlineVideo = lesson.templateType === LessonTemplateType.INLINE;
  // Inline-document template does the same for PDF & text: the document is
  // embedded on the page like a video instead of opening a separate page.
  const isInlineDocument = lesson.templateType === LessonTemplateType.INLINE_DOCUMENT;
  // Inline-playlist template: one hero player with a playlist of the lesson's
  // videos beside it; text/PDF/embed blocks render inline like INLINE_DOCUMENT.
  const isInlinePlaylist = lesson.templateType === LessonTemplateType.INLINE_PLAYLIST;
  // All inline templates share the page-width layout.
  const isInlineLayout = isInlineVideo || isInlineDocument || isInlinePlaylist;

  const hasSubLessons = (lesson.subLessonCount || 0) > 0 || (lesson.children && lesson.children.length > 0);

  // Hide draft/archived blocks (and FORM blocks whose linked form isn't
  // published) from members; reviewers in preview mode keep seeing them
  // (badged) so they can review the full lesson before publish.
  const sortedBlocks = [...(lesson.contentBlocks || [])]
    .sort((a, b) => a.position - b.position)
    .filter(b => {
      if (isPreview) {
        return true;
      }
      if (b.status !== EContentStatus.PUBLISHED) {
        return false;
      }
      return b.blockType !== ContentBlockType.FORM || b.formStatus === 'published';
    });

  // In preview, wrap a block's card with a Draft badge when it's unpublished.
  const withDraftBadge = (block: TContentBlockBase, node: React.ReactNode) => {
    if (!isPreview || block.status === EContentStatus.PUBLISHED) {
      return node;
    }
    return (
      <Box key={block.id} sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}>
          <DraftBadge />
        </Box>
        {node}
      </Box>
    );
  };
  // A finished live session with its replay available is video content, not an
  // event: the API marks those blocks `isReplay` and fills in the event's Vimeo id.
  // Classifying them here — rather than special-casing further down — is what makes
  // them obey the lesson template like any other video: hero player on the inline
  // template, a card in the "Video/s" section on the default one, and a line in the
  // "In this lesson" index either way.
  const isReplayBlock = (b: TContentBlockBase) => b.blockType === ContentBlockType.EVENT && b.isReplay === true;

  const videoBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.VIMEO || isReplayBlock(b));
  // Only sessions that have not yet produced a replay still render as their own
  // full-width band, with the countdown and join button.
  const eventBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.EVENT && !isReplayBlock(b));

  const documentBlocks = sortedBlocks.filter(
    b =>
      b.blockType === ContentBlockType.PDF ||
      b.blockType === ContentBlockType.TEXT ||
      b.blockType === ContentBlockType.REDIRECT ||
      b.blockType === ContentBlockType.EMBED ||
      b.blockType === ContentBlockType.FORM ||
      b.blockType === ContentBlockType.GCS_UPLOAD,
  );

  const quizBlocks = sortedBlocks.filter(b => b.blockType === ContentBlockType.QUIZ);

  // Inline templates render their own block types inline in position order —
  // text for INLINE (its videos render above the fold), text/PDF/embed for
  // INLINE_DOCUMENT. Whatever is left of the document-type blocks still shows
  // in the grouped "Documents" section below.
  const inlineDocumentBlockTypes = [ContentBlockType.TEXT, ContentBlockType.PDF, ContentBlockType.EMBED];
  const inlineMediaBlocks = sortedBlocks.filter(b =>
    isInlineDocument ? inlineDocumentBlockTypes.includes(b.blockType) : b.blockType === ContentBlockType.TEXT,
  );

  // Inline template: videos are lifted above the description row so they land
  // above the fold; only text blocks stay in the inline stack below it.
  const heroVideoBlocks = isInlineVideo && !hasSubLessons ? videoBlocks : [];
  const inlineBlockTypes = isInlineDocument ? inlineDocumentBlockTypes : [ContentBlockType.TEXT];

  // Inline-playlist template: one hero player above the fold; the playlist
  // beside it switches which video plays.
  const playlistVideoBlocks = isInlinePlaylist && !hasSubLessons ? videoBlocks : [];
  const [playlistVideoId, setPlaylistVideoId] = useState<string | null>(null);
  const playlistSelectedVideo = playlistVideoBlocks.find(b => b.id === playlistVideoId) ?? playlistVideoBlocks[0];
  const documentBlocksForView = isInlineLayout
    ? documentBlocks.filter(b => !inlineBlockTypes.includes(b.blockType))
    : documentBlocks;

  // The sidebar rail banner must span exactly the hero player iframe (per the
  // Figma design) — starting below the video title row, not at the hero
  // section's top. The player is 16:9 and fills the hero section's width, and
  // it is the section's last child, so both its height and its offset below
  // the title row follow from the section's measured size.
  const hasHeroVideo = heroVideoBlocks.length > 0;
  const heroSectionRef = useRef<HTMLDivElement>(null);
  const [heroSize, setHeroSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const section = heroSectionRef.current;
    if (!section) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setHeroSize({ width: section.offsetWidth, height: section.offsetHeight }),
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // The "Video not available" fallback is a fixed 400px box, so never exceed
  // the section itself.
  const heroPlayerHeight = Math.min((heroSize.width * 9) / 16, heroSize.height);
  const heroPlayerBox =
    hasHeroVideo && heroSize.width > 0
      ? { height: heroPlayerHeight, offsetTop: heroSize.height - heroPlayerHeight }
      : null;

  const videoSectionHeading = lesson.videoSectionTitle?.trim() || 'Video/s';
  const documentSectionHeading = lesson.documentSectionTitle?.trim() || 'Documents';
  const quizSectionHeading = lesson.quizSectionTitle?.trim() || 'Quiz';

  // The sidebar banner card renders at its natural design width on desktop.
  const SIDEBAR_BANNER_WIDTH = 240;

  // The default and inline templates align every row to the top section's
  // width (left card 631 + 24 gap + summary 368 = 1023) so the video, documents
  // and dividers all share the same right edge. The inline-playlist Figma (node 4882:2254) sizes its
  // frames "Fill" inside a 918px container — rows stretch up to that cap, with
  // the playlist (220) and summary (342) columns fixed and the
  // player/description filling.
  const INLINE_ROW_MAX = 1023;
  const PLAYLIST_ROW_MAX = 918;
  const summaryColumnWidth = isInlinePlaylist ? 342 : 368;
  // The default template's rows (videos strip, documents card, dividers) used to
  // stop 190px short of the top row's right edge; they now fill it like the
  // inline template so every section lines up with the description + summary.
  const rowWidthSx = isInlinePlaylist
    ? { width: '100%', maxWidth: { md: PLAYLIST_ROW_MAX } }
    : { width: '100%', maxWidth: { md: INLINE_ROW_MAX } };

  return (
    <Box>
      {/* Sticky nav must be a direct child of the page box: `position: sticky`
          can't escape its parent, so nesting it in the top-section wrapper made
          it scroll away once that section left the viewport. */}
      {breadcrumbs}

      {/* Two-column desktop layout: the main content column plus, from
          SIDEBAR_BANNER_DESKTOP_MIN_WIDTH up, the sidebar banner rail beside
          it. The rail is the PromoBannerSlot itself so it only takes space
          when a banner is actually assigned. */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* The column is capped at the top row's width (description 631 + 24 gap
            + summary 368 = 1023) rather than `fit-content`: a flex item's
            intrinsic width comes from its text, not its flex-basis, so under a
            fit-content parent a short description shrank the column — and every
            row below it (player, cards, dividers) — to the text's width. The
            fill-width playlist template stretches to the page. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: isInlinePlaylist ? '100%' : { xs: '100%', md: INLINE_ROW_MAX },
          }}
        >
          <Box>
            {/* Inline template: the player sits directly under the breadcrumbs so
            the video is above the fold, ahead of the lesson sections. */}
            {heroVideoBlocks.length > 0 && (
              <Box ref={heroSectionRef} sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3, ...rowWidthSx }}>
                {heroVideoBlocks.map(block =>
                  withDraftBadge(block, <VideoBlockPlayer key={block.id} block={block} lessonId={lesson.id} hero />),
                )}
              </Box>
            )}

            {/* Inline-playlist template: a transparent section (no card — the
            header and player sit on the page background) with the full-width
            header row (title / instructor / duration / watched chips), the
            hero player on the left and the playlist card beside it (Figma
            keeps the playlist narrower than the summary column below —
            683:220 of the 918 row). Clicking a playlist row swaps the player
            (remounted via key so tracking resets per video). On mobile the
            playlist card renders below the description instead — see the
            description row. */}
            {playlistSelectedVideo && (
              <Box sx={{ mb: 3, ...rowWidthSx }}>
                {withDraftBadge(
                  playlistSelectedVideo,
                  <VideoBlockPlayer
                    key={playlistSelectedVideo.id}
                    block={playlistSelectedVideo}
                    lessonId={lesson.id}
                    hero
                    flush
                    aside={
                      <Box
                        sx={{
                          display: { xs: 'none', md: 'block' },
                          // Playlist column width from the Figma frame (683:220).
                          flex: '0 0 220px',
                          minWidth: 0,
                          // The column stretches to the row (= player) height,
                          // and the absolutely-positioned playlist fills it
                          // without adding to it — so the playlist card always
                          // matches the player's height exactly and scrolls
                          // internally when its rows overflow.
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ position: 'absolute', inset: 0 }}>
                          <VideoPlaylist
                            videoBlocks={playlistVideoBlocks}
                            lessonId={lesson.id}
                            selectedBlockId={playlistSelectedVideo.id}
                            onSelect={block => setPlaylistVideoId(block.id)}
                          />
                        </Box>
                      </Box>
                    }
                  />,
                )}
              </Box>
            )}

            {/* Inline-playlist: the row is capped at the same 918 Figma frame as
            the player card, the description fills the space the fixed summary
            column leaves, so both cards share the player card's edges. */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                ...(isInlinePlaylist && rowWidthSx),
              }}
            >
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: isInlinePlaylist ? '1 1 auto' : '1 1 631px' },
                  maxWidth: { xs: '100%', md: isInlinePlaylist ? 'none' : 631 },
                  minWidth: 0,
                  bgcolor: 'white',
                  borderRadius: '15px',
                  p: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Typography
                    variant="h5"
                    sx={{ fontSize: '16px', fontWeight: 'bold', color: theme.palette.common.black }}
                  >
                    {lesson.title}
                  </Typography>
                  {isPreview && lesson.status !== LessonStatus.PUBLISHED && <DraftBadge />}
                  {hasSubLessons && (
                    <Chip
                      icon={<MdFolder size={16} />}
                      label={`${lesson.subLessonCount || lesson.children?.length || 0} sub-lessons`}
                      size="small"
                      color="secondary"
                      variant="filled"
                    />
                  )}
                </Box>

                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    ...richHtmlSx,
                    color: theme.palette.common.black,
                    lineHeight: 1.6,
                    fontSize: '14px',
                    whiteSpace: 'pre-line',
                    ...(isDescriptionCollapsed && {
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 5,
                      overflow: 'hidden',
                    }),
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(lesson.longDescription || 'No description available'),
                  }}
                />

                {isMobile && !!lesson.longDescription && (
                  <Button
                    variant="text"
                    disableRipple
                    onClick={() => setIsDescriptionExpanded(prev => !prev)}
                    endIcon={isDescriptionExpanded ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                    sx={{ px: 0, mt: 1, fontSize: '14px', fontWeight: 'bold', '&:hover': { background: 'none' } }}
                  >
                    {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                  </Button>
                )}
              </Box>

              {/* Inline-playlist template, mobile only: the playlist stacks as
              its own labeled card between the description and the summary
              (Figma mobile order: player → description → playlist → summary). */}
              {playlistSelectedVideo && (
                <Box sx={{ display: { xs: 'block', md: 'none' }, flex: '1 1 100%' }}>
                  <VideoPlaylist
                    videoBlocks={playlistVideoBlocks}
                    lessonId={lesson.id}
                    selectedBlockId={playlistSelectedVideo.id}
                    onSelect={block => setPlaylistVideoId(block.id)}
                  />
                </Box>
              )}

              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: `0 0 ${summaryColumnWidth}px` },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                }}
              >
                <LessonContentSummary
                  lesson={lesson}
                  hasSubLessons={!!hasSubLessons}
                  // The playlist beside the player already lists every video —
                  // keep the summary card to documents/quiz/discussion only.
                  videoBlocks={isInlinePlaylist ? [] : videoBlocks}
                  documentBlocks={documentBlocks}
                  quizBlocks={quizBlocks}
                  onNavigateToBlock={onNavigateToBlock}
                />

                {showProgressBar && <ProgressCard progressPercent={progressPercent} />}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 4, ...rowWidthSx }} />

          {eventBlocks.length > 0 && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...rowWidthSx }}>
                {eventBlocks.map(block =>
                  withDraftBadge(
                    block,
                    <LiveSessionCard
                      key={block.id}
                      contentBlockId={block.id}
                      bannerUrl={block.textBannerUrl}
                      instructorId={block.instructorId}
                    />,
                  ),
                )}
              </Box>
              <Divider sx={{ my: 4, ...rowWidthSx }} />
            </>
          )}

          {hasSubLessons && <SubLessonsSection parentId={lesson.id} onNavigateToSubLesson={onNavigateToSubLesson} />}

          {hasSubLessons && <Divider sx={{ my: 4, ...rowWidthSx }} />}

          {/* Inline template: render video & text on the page in position order. Other
          block types fall through to the grouped Documents/Quiz sections below. */}
          {!hasSubLessons && isInlineLayout && inlineMediaBlocks.length > 0 && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, ...rowWidthSx }}>
                {inlineMediaBlocks.map(block => {
                  let card: React.ReactNode;
                  if (block.blockType === ContentBlockType.VIMEO) {
                    card = <VideoBlockPlayer key={block.id} block={block} lessonId={lesson.id} compact />;
                  } else if (block.blockType === ContentBlockType.PDF) {
                    card = <PdfBlockViewer key={block.id} block={block} />;
                  } else if (block.blockType === ContentBlockType.EMBED) {
                    card = <EmbedBlockContent key={block.id} block={block} />;
                  } else {
                    card = <TextBlockContent key={block.id} block={block} lessonId={lesson.id} fullWidth />;
                  }
                  return withDraftBadge(block, card);
                })}
              </Box>
              {(documentBlocksForView.length > 0 || quizBlocks.length > 0) && <Divider sx={{ my: 4, ...rowWidthSx }} />}
            </>
          )}

          {!hasSubLessons && !isInlineVideo && !isInlinePlaylist && videoBlocks.length > 0 && (
            <>
              <Box sx={rowWidthSx}>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
                >
                  {videoSectionHeading}
                </Typography>
                <Box sx={{ py: 1, display: 'flex', gap: 3, overflowX: 'auto' }}>
                  {videoBlocks.map(block =>
                    withDraftBadge(
                      block,
                      <VideoCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />,
                    ),
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 4, ...rowWidthSx }} />
            </>
          )}

          {!hasSubLessons && documentBlocksForView.length > 0 && (
            <>
              <Box
                pt={3}
                pb={4}
                sx={{
                  backgroundColor: theme.palette.common.white,
                  ...rowWidthSx,
                  borderRadius: '15px',
                  p: 3,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
                >
                  {documentSectionHeading}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2.25, flexWrap: 'wrap' }}>
                  {documentBlocksForView.map(block => {
                    let card: React.ReactNode;
                    if (block.blockType === ContentBlockType.PDF) {
                      card = <DocumentCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                    } else if (block.blockType === ContentBlockType.REDIRECT) {
                      card = <RedirectCard key={block.id} block={block} />;
                    } else if (block.blockType === ContentBlockType.EMBED) {
                      card = <EmbedCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                    } else if (block.blockType === ContentBlockType.FORM) {
                      card = <FormCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                    } else if (block.blockType === ContentBlockType.GCS_UPLOAD) {
                      card = <GcsUploadCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                    } else {
                      card = <TextCard key={block.id} block={block} onClick={() => onNavigateToBlock(block)} />;
                    }
                    return withDraftBadge(block, card);
                  })}
                </Box>
              </Box>
              <Divider sx={{ my: 4, ...rowWidthSx }} />
            </>
          )}

          {!hasSubLessons && quizBlocks.length > 0 && (
            <Box sx={{ pb: 4, ...rowWidthSx }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.common.black, mb: 1.5 }}
              >
                {quizSectionHeading}
              </Typography>
              {quizBlocks.map(block =>
                withDraftBadge(
                  block,
                  <QuizCard
                    key={block.id}
                    title={block.title || 'Quiz'}
                    quizId={block.quizId || ''}
                    onClick={() => onNavigateToBlock(block)}
                  />,
                ),
              )}
            </Box>
          )}

          {/* Promo banner — footer. Sticks to the bottom of the viewport on mobile,
          renders inline at the end of the page on desktop. */}
          <PromoBannerSlot
            entityId={lesson.id}
            position={PromoBannerPosition.FOOTER}
            sx={{
              ...rowWidthSx,
              // position: { xs: 'sticky', sm: 'sticky', md: 'static' },
              bottom: { xs: 16, md: 'auto' },
              zIndex: { xs: theme.zIndex.appBar, md: 'auto' },
            }}
          />

          {/* Promo banner — sidebar, narrow fallback. Below
          SIDEBAR_BANNER_DESKTOP_MIN_WIDTH there's no room for the rail beside
          the content column, so the banner becomes a full-width bar stuck to
          the bottom of the viewport instead. The cutoff must match the banner
          component's own layout switch (SidebarPromoBannerWithLaurenImage) —
          the full-width mobile bar breaks inside the 240px card box and vice
          versa. */}
          <PromoBannerSlot
            entityId={lesson.id}
            position={PromoBannerPosition.SIDEBAR}
            sx={{
              position: 'sticky',
              bottom: 16,
              width: '100%',
              my: 2,
              zIndex: theme.zIndex.appBar,
              [theme.breakpoints.up(SIDEBAR_BANNER_DESKTOP_MIN_WIDTH)]: {
                display: 'none',
              },
            }}
          />
        </Box>

        {/* Promo banner — sidebar rail. On wide desktops the vertical card sits
            in flow beside the content column (no overlap — the column shrinks
            to make room) and stays put at the top of the rail — it does not
            follow the scroll. */}
        <PromoBannerSlot
          entityId={lesson.id}
          position={PromoBannerPosition.SIDEBAR}
          sx={{
            display: 'none',
            [theme.breakpoints.up(SIDEBAR_BANNER_DESKTOP_MIN_WIDTH)]: {
              display: 'block',
            },
            width: SIDEBAR_BANNER_WIDTH,
            flexShrink: 0,
            alignSelf: 'flex-start',
            position: 'static',
            my: 0,
            mr: { lg: 0, xl: 18 },
            // On hero-video lessons the promo card spans exactly the player
            // iframe (measured via heroSectionRef): pushed down past the video
            // title row and sized to the player box. Other lessons keep the
            // card's natural height.
            ...(heroPlayerBox !== null && { height: heroPlayerBox.height, mt: `${heroPlayerBox.offsetTop}px` }),
          }}
        />
      </Box>
    </Box>
  );
}
